import { useMemo } from "react";
import {
  ShieldAlert, ShieldX, Search, Radar, Eye, CheckCircle2, Info, Sparkles,
} from "lucide-react";
import { fdate } from "@/lib/format";

/**
 * SenalesRiesgoTimeline — Historial de señales de riesgo del cliente
 * ------------------------------------------------------------------
 * Timeline vertical ordenado (más reciente arriba) que muestra cada señal con:
 *   - fuente (SAT 69, LPB, OFAC, ONU, Q-Detect, barrido diario, Lista Vigilancia FELVORA…)
 *   - fecha
 *   - por qué coincidió (campo: RFC / CURP / nombre)
 *   - motivo / categoría / score
 *
 * Distingue explícitamente:
 *   - SEÑAL OFICIAL (autoridad) — badge rojo/naranja "Autoridad · Lista oficial"
 *   - SEÑAL INTERNA (Vigilancia FELVORA) — badge indigo/amber "Plataforma FELVORA · Lista interna"
 *
 * Al Oficial le importa la diferencia: una señal de la UIF/OFAC no es lo mismo que una
 * alta interna nuestra. El componente hace el desglose visual antes de que decida.
 *
 * NO consulta backend adicional — todos los datos vienen del payload `perfil360`.
 * NO toca el motor.
 */
export function SenalesRiesgoTimeline({ cliente, perfil360 }) {
  const senales = useMemo(() => {
    if (!cliente) return [];
    const items = [];

    // 1) Señales OFICIALES (autoridad / lista publicada)
    //    (a) Bloqueo automático activo por lista oficial (SAT 69, LPB, OFAC, ONU).
    if (cliente.bloqueado_lpb) {
      const lista = cliente.bloqueo_lista_label || cliente.bloqueo_lista || "Lista de sanción";
      items.push({
        kind: "oficial",
        subtipo: "sancion_activa",
        titulo: `${lista} — bloqueo activo`,
        badge: "Autoridad · Lista oficial",
        icono: ShieldX,
        color: "red",
        at: cliente.bloqueo_desde || cliente.bloqueo_fecha || cliente.updated_at,
        campo: "confirmado por OC",
        score: cliente.bloqueo_score || null,
        motivo: cliente.bloqueo_motivo || cliente.riesgo_motivo || "Coincidencia crítica en lista de sanción publicada por autoridad.",
        fuente_detalle: lista,
      });
    }
    //    (b) Desbloqueo histórico documentado con carta.
    if (!cliente.bloqueado_lpb && cliente.desbloqueo_por) {
      items.push({
        kind: "oficial",
        subtipo: "desbloqueo",
        titulo: `Desbloqueo por sanción — decisión documentada`,
        badge: "Autoridad · Determinación OC",
        icono: CheckCircle2,
        color: "emerald",
        at: cliente.desbloqueo_fecha || cliente.desbloqueo_at || cliente.updated_at,
        campo: `por ${cliente.desbloqueo_por}`,
        motivo: cliente.desbloqueo_motivo || "Desbloqueo autorizado con carta PDF.",
        fuente_detalle: cliente.desbloqueo_lista_previa || cliente.bloqueo_lista_label || "",
      });
    }
    //    (c) Otros orígenes oficiales conocidos (screening_qeq, barrido_diario). Solo si
    //    el `riesgo_origen` primario apunta ahí y no ya cubierto por bloqueo activo.
    const origen = cliente.riesgo_origen || "";
    if (!cliente.bloqueado_lpb) {
      if (origen === "screening_qeq") {
        items.push({
          kind: "oficial",
          subtipo: "qdetect",
          titulo: "Coincidencia en cotejo externo (proveedor externo)",
          badge: "Autoridad · Cotejo externo",
          icono: Search,
          color: "orange",
          at: cliente.updated_at,
          motivo: cliente.riesgo_motivo || "Coincidencia detectada por cotejo externo.",
          fuente_detalle: "Cotejo externo",
        });
      } else if (origen === "barrido_diario") {
        items.push({
          kind: "oficial",
          subtipo: "barrido",
          titulo: "Coincidencia detectada en barrido diario centralizado",
          badge: "Autoridad · Barrido diario",
          icono: Radar,
          color: "orange",
          at: cliente.updated_at,
          motivo: cliente.riesgo_motivo || "Detección durante barrido diario centralizado contra listas oficiales vigentes.",
          fuente_detalle: "Barrido diario",
        });
      } else if (origen === "screening_externo") {
        items.push({
          kind: "oficial",
          subtipo: "screening_ext",
          titulo: "Coincidencia crítica en lista oficial",
          badge: "Autoridad · Lista oficial",
          icono: ShieldAlert,
          color: "red",
          at: cliente.updated_at,
          motivo: cliente.riesgo_motivo || "Coincidencia crítica confirmada en lista publicada por autoridad.",
          fuente_detalle: cliente.bloqueo_lista_label || cliente.bloqueo_lista || "Lista oficial",
        });
      }
    }

    // 2) Señales INTERNAS (Lista de Vigilancia FELVORA) + Barrido diario centralizado.
    //    Ambas viven en el mismo array `señales_riesgo` pero se distinguen por `sub_fuente`
    //    y por `fuente` — Vigilancia usa fuentes internas indigo/amber; Barrido usa
    //    `fuente=screening_externo, sub_fuente=barrido_diario` (rojo/naranja, autoridad).
    const senalesArr = Array.isArray(cliente["señales_riesgo"])
      ? cliente["señales_riesgo"] : (cliente.senales_riesgo || []);
    senalesArr.forEach((s, idx) => {
      const isBarrido = s.sub_fuente === "barrido_diario";
      const isPep = s.es_pep === true || s.fuente === "vigilancia_felvora_pep";
      const cat = s.categoria || "categoría no especificada";
      const fuenteOrigen = s.fuente_origen || "origen no especificado";
      const refFuente = s.referencia_fuente || "";
      if (isBarrido) {
        // Señal OFICIAL vía barrido diario (proveedor externo). Se agrupa con las de autoridad.
        items.push({
          kind: "oficial",
          subtipo: isPep ? "barrido_pep" : "barrido",
          titulo: isPep
            ? `Barrido diario · ${s.proveedor || "proveedor externo"} — PEP detectado`
            : `Barrido diario · ${s.proveedor || "proveedor externo"}`,
          badge: isPep
            ? "Autoridad · Barrido diario · PEP"
            : "Autoridad · Barrido diario centralizado",
          icono: Radar,
          color: isPep ? "amber" : "orange",
          at: s.at,
          campo: s.detalles_proveedor
            ? `resultado del proveedor: "${String(s.detalles_proveedor).slice(0, 80)}"`
            : "coincidencia externa",
          score: s.score || null,
          motivo: s.motivo || "",
          fuente_detalle: (
            `${s.proveedor || "proveedor externo"} · corte listas ${s.fecha_lista_cotejo || s.fecha_corte || "—"}`
          ),
          entrada_nombre: s.entrada_nombre || "",
          idx,
        });
      } else {
        items.push({
          kind: "interna",
          subtipo: isPep ? "pep" : "vigilancia",
          titulo: isPep
            ? `PEP — Persona Políticamente Expuesta`
            : `Señal en Lista de Vigilancia FELVORA — ${cat}`,
          badge: isPep
            ? "Plataforma FELVORA · Lista interna · PEP"
            : "Plataforma FELVORA · Lista interna",
          icono: isPep ? Sparkles : Eye,
          color: isPep ? "amber" : "indigo",
          at: s.at,
          campo: s.campo_coincidente
            ? (s.campo_coincidente === "nombre"
                ? "coincidencia por nombre (verificar homónimo)"
                : `coincidencia por ${s.campo_coincidente.toUpperCase()}`)
            : "",
          score: s.score || null,
          motivo: s.motivo || "",
          fuente_detalle: refFuente ? `${fuenteOrigen} · ${refFuente}` : fuenteOrigen,
          entrada_nombre: s.entrada_nombre || "",
          idx,
        });
      }
    });

    // Orden descendente por fecha (más reciente arriba). Los sin fecha al final.
    return items.sort((a, b) => {
      if (!a.at) return 1;
      if (!b.at) return -1;
      return String(b.at).localeCompare(String(a.at));
    });
  }, [cliente]);

  if (!cliente) return null;

  const oficiales = senales.filter((s) => s.kind === "oficial").length;
  const internas = senales.filter((s) => s.kind === "interna").length;

  if (senales.length === 0) {
    return (
      <div
        data-testid="senales-riesgo-empty"
        className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
      >
        <Info className="mx-auto mb-2 h-5 w-5 text-slate-400" />
        Sin señales de riesgo registradas para este cliente.
      </div>
    );
  }

  return (
    <div data-testid="senales-riesgo-timeline" className="space-y-4">
      {/* Header con contadores y leyenda de tipos */}
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <p className="font-heading text-sm font-semibold text-slate-800">
            Historial de señales de riesgo
          </p>
          <p className="text-xs text-slate-500">
            Cronología completa · el Oficial debe revisar todas antes de decidir bloqueo o descarte.
          </p>
        </div>
        <div className="flex gap-2 text-xs">
          <span
            data-testid="senales-count-oficiales"
            className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 font-medium text-red-700"
          >
            {oficiales} de autoridad
          </span>
          <span
            data-testid="senales-count-internas"
            className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 font-medium text-indigo-700"
          >
            {internas} internas
          </span>
        </div>
      </div>

      {/* Leyenda de tipos */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-800">
          <ShieldX className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            <b className="font-semibold uppercase tracking-wide">Autoridad · Lista oficial</b>
            <br />
            SAT 69, LPB (UIF), OFAC, ONU, cotejo externo, barrido de listas. Origen verificable
            publicado por autoridad o cotejo externo. La evidencia va al Anexo XVIII.
          </p>
        </div>
        <div className="flex items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-[11px] text-indigo-800">
          <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            <b className="font-semibold uppercase tracking-wide">Plataforma FELVORA · Lista interna</b>
            <br />
            Alta manual de vigilancia dada por el Super Administrador de plataforma. No es
            autoridad — usa la fuente indicada para verificar el registro original.
          </p>
        </div>
      </div>

      {/* Timeline vertical */}
      <ol className="relative border-l-2 border-dashed border-slate-200 pl-6">
        {senales.map((s, i) => {
          const Icono = s.icono;
          const colorMap = {
            red: {
              dot: "bg-red-100 border-red-400 text-red-700",
              card: "border-red-200 bg-red-50/70",
              title: "text-red-900",
              badge: "border-red-300 bg-red-100 text-red-800",
              text: "text-red-800",
            },
            orange: {
              dot: "bg-orange-100 border-orange-400 text-orange-700",
              card: "border-orange-200 bg-orange-50/70",
              title: "text-orange-900",
              badge: "border-orange-300 bg-orange-100 text-orange-800",
              text: "text-orange-800",
            },
            emerald: {
              dot: "bg-emerald-100 border-emerald-400 text-emerald-700",
              card: "border-emerald-200 bg-emerald-50/70",
              title: "text-emerald-900",
              badge: "border-emerald-300 bg-emerald-100 text-emerald-800",
              text: "text-emerald-800",
            },
            amber: {
              dot: "bg-amber-100 border-amber-400 text-amber-700",
              card: "border-amber-200 bg-amber-50/70",
              title: "text-amber-900",
              badge: "border-amber-300 bg-amber-100 text-amber-800",
              text: "text-amber-800",
            },
            indigo: {
              dot: "bg-indigo-100 border-indigo-400 text-indigo-700",
              card: "border-indigo-200 bg-indigo-50/70",
              title: "text-indigo-900",
              badge: "border-indigo-300 bg-indigo-100 text-indigo-800",
              text: "text-indigo-800",
            },
          };
          const c = colorMap[s.color] || colorMap.indigo;
          const testId = `senal-${s.kind}-${i}`;
          return (
            <li key={testId} className="mb-4" data-testid={testId}>
              <span
                className={`absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full border-2 ${c.dot}`}
              >
                <Icono className="h-3 w-3" />
              </span>
              <div className={`rounded-lg border ${c.card} px-4 py-3`}>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${c.badge}`}
                        data-testid={`senal-tipo-badge-${i}`}
                      >
                        {s.kind === "oficial" ? "Autoridad" : "Plataforma FELVORA"}
                      </span>
                      <span className={`text-[10px] font-medium ${c.text}`}>
                        {s.badge}
                      </span>
                    </div>
                    <p className={`mt-1 font-heading text-sm font-semibold ${c.title}`}>
                      {s.titulo}
                    </p>
                    {s.fuente_detalle && (
                      <p className={`mt-0.5 text-xs ${c.text}`}>
                        Fuente: <span className="font-medium">{s.fuente_detalle}</span>
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-medium text-slate-700">
                      {s.at ? fdate(s.at) : "—"}
                    </p>
                    {s.score && (
                      <p className="text-[11px] text-slate-500">
                        score{" "}
                        <span className={`font-bold ${c.text}`}>{s.score}</span>
                      </p>
                    )}
                  </div>
                </div>
                {s.campo && (
                  <p className={`mt-2 text-xs italic ${c.text}`}>
                    <b className="not-italic">Motivo del match:</b> {s.campo}
                  </p>
                )}
                {s.motivo && (
                  <p className="mt-2 border-t border-slate-200/50 pt-2 text-[11px] leading-relaxed text-slate-700">
                    {s.motivo}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        Las señales de <b>autoridad</b> tienen respaldo documental en el Anexo XVIII y son
        publicadas por la UIF, SAT, OFAC, ONU o el proveedor externo. Las señales <b>internas</b>{" "}
        son altas manuales del Super Administrador de plataforma — usa la fuente indicada
        para verificar el registro original antes de decidir.
      </p>
    </div>
  );
}
